import { useState } from 'react';
import type { JSX } from 'react';

import type { PrefetchedDataProps } from 'src/shared/prefetch';
import { withPrefetchedData } from 'src/shared/prefetch';

import type { PetitionHeadlineBanditData, Sharer } from 'src/app/pages/petitionDetails/shared/types';

import { ShareHeadlineProvider, useDefaultShareHeadline } from '../../hooks/shareHeadline';
import { getFacebookBanditVariantText, getSharer } from '../api';
import { CommonPetitionDetails } from '../CommonPetitionDetails';

type PrefetchedData = Readonly<{
	sharer?: Sharer;
	variant?: PetitionHeadlineBanditData;
}>;

export const PetitionDetailsFacebookBandit = withPrefetchedData(
	({ prefetchedData }: PrefetchedDataProps<PrefetchedData>): JSX.Element => {
		// this allows us to conserve the sharer after first render
		const [sharer] = useState<Sharer | undefined>(prefetchedData?.sharer);
		const [variant] = useState<PetitionHeadlineBanditData | undefined>(prefetchedData?.variant);

		const { title, description } = useDefaultShareHeadline();

		if (!sharer) {
			return <CommonPetitionDetails />;
		}

		if (!variant) {
			return (
				<ShareHeadlineProvider sharer={sharer} title={title} description={description}>
					<CommonPetitionDetails />
				</ShareHeadlineProvider>
			);
		}

		return (
			<ShareHeadlineProvider sharer={sharer} title={variant.headline} description={variant.description}>
				<CommonPetitionDetails />
			</ShareHeadlineProvider>
		);
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchDependencies: [CommonPetitionDetails],
		prefetchData: async (context) => {
			const {
				params: { slug, experimentName, experimentVariantName, sharerUserId },
			} = context;

			if (!(slug && experimentName && experimentVariantName && sharerUserId)) return {};

			return {
				variant: await getFacebookBanditVariantText(slug, experimentVariantName, context.utilityContext).catch(
					() => undefined,
				),
				sharer: await getSharer(sharerUserId, context.utilityContext).catch(() => undefined),
			};
		},
	},
);
